import React from 'react';
import { get, isNumber } from 'lodash';
import gettext from '@eb/gettext';
import { ANCHOR_LINK, DIVIDER, ROUTER_LINK } from '@eb/eds-nav-list-item';
import { getStaffUserMenuItems, getUserMenuItemsLookup } from '@eb/site-header';
import { trackAnalyticsEvent } from '@eb/site-analytics';
import {
    SETTINGS_HOME_URL_PATH,
    EVENT_IMAGE_PLACEHOLDER_URL,
    ORGANIZATION_PROFILE_CATEGORY,
    ORGANIZATION_SETTINGS_DROPDOWN_CLICKED,
} from './constants';
import { getFullOrganizationsUrl } from './routing';
import { formatNumber } from '@eb/intl';

import { GearChunky } from '@eb/eds-iconography';
import { DoubleArrowChunky } from '@eb/eds-iconography';
import { CalendarChunky } from '@eb/eds-iconography';
import { Badge } from '@eb/eds-badge';

const MENU_ITEM_ICON_COLOR = 'grey-500';

/**
 * Format a number, with comma-separated thousands and custom precision/decimal places and suffix for every unit of the divisor
 *
 *  formatNumberWithSuffix(
 *      8955212478,
 *      '¤ #,##0.00',
 *      {precision: 0},
 *      {precision: 1},
 *      1000000,
 *      'M'
 *  )
 *  8,955.2M
 *
 *  formatNumberWithSuffix(
 *      212478,
 *      '¤ #,##0.00',
 *      {precision: 0},
 *      {precision: 1},
 *      1000000,
 *      'M'
 *  )
 *  212,478
 *
 * @param {number|string} value The value to format
 * @param {string} [currencyFormat] Format of currency, e.g. '¤ #,##0.00'
 * @param {object} [options] Additional options to override currency format info
 * @param {object} [suffixOptions] Additional options to override currency format info when value is displayed with suffix
 * @param {number} [divisor] Suffix is displayed when value is equal or larger than divisor
 * @param {string} [suffix] Suffix to be added when value exceeds divisor
 * @return {string} Localized formatted number
 */
export const formatNumberWithSuffix = (
    value,
    currencyFormat,
    options,
    suffixOptions,
    divisor,
    suffix,
) => {
    const formatValue = value ? value : 0;
    const isValueLargerorEqualToDivisor = formatValue >= divisor;
    const isDivisorLargerThanZero = divisor > 0;
    const formattedNumber = formatNumber(formatValue, currencyFormat, options);
    const formattedNumbetWithSuffix = `${formatNumber(
        formatValue / divisor,
        currencyFormat,
        suffixOptions,
    )}${suffix}`;

    return isDivisorLargerThanZero && isValueLargerorEqualToDivisor
        ? formattedNumbetWithSuffix
        : formattedNumber;
};

export const formatNumberWithM = (
    value,
    currencyFormat,
    options = { precision: 0 },
    suffixOptions = { precision: 1 },
) =>
    formatNumberWithSuffix(
        value,
        currencyFormat,
        options,
        suffixOptions,
        1000000,
        'M',
    );

export const SecondaryContentWithBadge = ({
    followerCount,
    currencyFormat,
}) => {
    const formattedFollowerCount = formatNumberWithM(
        followerCount,
        currencyFormat,
    );

    let message = gettext('%(formattedFollowerCount)s followers', {
        formattedFollowerCount,
    });

    if (!followerCount || followerCount === 0) {
        message = gettext('No followers yet');
    }

    return (
        <div className="eds-align--center-vertical eds-l-mar-top-1">
            <p className="eds-text-bs eds-text-color--grey-600 eds-l-pad-right-1">
                {message}
            </p>
            <Badge text={gettext('New')} bgColor="success" />
        </div>
    );
};

const _getOrganizationUserMenuItems = ({
    userMenuItemsLookup,
    hideOrganizationSwitch,
    onlyUseAnchorLinks,
    showEventsLink,
    deployFollowerInsightsOrganization,
    followerCount,
    userPartnerId,
    currencyFormat,
    canUseStudio,
}) => {
    let organizationSettingsSecondaryContent;
    let trackSettingsDropdownClick;

    if (deployFollowerInsightsOrganization) {
        organizationSettingsSecondaryContent =
            followerCount === null ? null : (
                <SecondaryContentWithBadge
                    followerCount={followerCount}
                    currencyFormat={currencyFormat}
                />
            );
        trackSettingsDropdownClick = () =>
            trackAnalyticsEvent({
                dimensions: { userId: userPartnerId },
                action: ORGANIZATION_SETTINGS_DROPDOWN_CLICKED,
                category: ORGANIZATION_PROFILE_CATEGORY,
            });
    }

    const menuItems = [
        {
            type: ANCHOR_LINK,
            onClick: trackSettingsDropdownClick,
            content: gettext('Organization Settings'),
            secondaryContent: organizationSettingsSecondaryContent,
            path: getFullOrganizationsUrl(SETTINGS_HOME_URL_PATH),
            iconType: <GearChunky />,
            iconColor: MENU_ITEM_ICON_COLOR,
        },
    ];

    const organizationSwitchLinkType = onlyUseAnchorLinks
        ? ANCHOR_LINK
        : ROUTER_LINK;

    if (!hideOrganizationSwitch) {
        menuItems.push({
            type: organizationSwitchLinkType,
            content: gettext('Switch Organization'),
            path: getFullOrganizationsUrl(),
            iconType: <DoubleArrowChunky />,
            iconColor: MENU_ITEM_ICON_COLOR,
        });
    }

    if (showEventsLink) {
        menuItems.unshift({
            type: ANCHOR_LINK,
            content: gettext('Events'),
            path: getFullOrganizationsUrl('events'),
            iconType: <CalendarChunky />,
            iconColor: MENU_ITEM_ICON_COLOR,
        });
    }

    if (canUseStudio) {
        menuItems.push(userMenuItemsLookup.studio);
    }

    menuItems.push({
        type: DIVIDER,
    });

    return menuItems;
};

const _getAccountUserMenuItems = ({
    userMenuItemsLookup,
    numFollowing,
    featureFlags = {},
}) => {
    const {
        accountSettingsWithIcon,
        tickets,
        saved,
        following,
        credits,
    } = userMenuItemsLookup;
    let userMenuItems = [accountSettingsWithIcon, tickets, saved];

    if (featureFlags.launchCreatorCreditsSectionAttendee) {
        userMenuItems = [accountSettingsWithIcon, credits, tickets, saved];
    }

    if (isNumber(numFollowing)) {
        userMenuItems.push(following);
    }

    userMenuItems.push({ type: DIVIDER });

    return userMenuItems;
};

const _getUserMenuItems = ({
    showEventsLink,
    hideOrganizationSwitch,
    publicUserId,
    requestPath,
    serverUrl,
    email,
    fullName,
    userImageUrl,
    isStaff,
    adminServerUrl,
    hasTikibar,
    logoutUrl,
    numOrders,
    numSavedEvents,
    numFollowing,
    onlyUseAnchorLinks,
    deployFollowerInsightsOrganization,
    followerCount,
    userPartnerId,
    currencyFormat,
    canUseStudio,
    featureFlags,
}) => {
    const accountSettingsPath = 'account-settings';
    const userMenuItemsLookup = getUserMenuItemsLookup({
        adminServerUrl,
        logoutUrl,
        numOrders,
        numSavedEvents,
        numFollowing,
        publicId: publicUserId,
        requestPath,
        serverUrl,
        accountSettingsPath,
        email,
        fullName,
        userImageUrl,
    });
    const organizationMenuItems = _getOrganizationUserMenuItems({
        userMenuItemsLookup,
        hideOrganizationSwitch,
        onlyUseAnchorLinks,
        showEventsLink,
        deployFollowerInsightsOrganization,
        followerCount,
        userPartnerId,
        currencyFormat,
        canUseStudio,
    });
    const accountMenuItems = _getAccountUserMenuItems({
        userMenuItemsLookup,
        numFollowing,
        featureFlags,
    });
    const staffMenuItems = getStaffUserMenuItems({
        userMenuItemsLookup,
        hasTikibar,
        isStaff,
    });

    return [
        ...organizationMenuItems,
        ...accountMenuItems,
        ...staffMenuItems,
        userMenuItemsLookup.logout,
    ];
};

export const getUserMenuInfo = ({
    showEventsLink = false,
    organizationName,
    organizationImageUrl,
    hideOrganizationSwitch,
    publicUserId,
    requestPath,
    serverUrl,
    email,
    fullName,
    userImageUrl,
    isStaff,
    adminServerUrl,
    hasTikibar,
    logoutUrl,
    numOrders,
    numSavedEvents,
    numFollowing,
    onlyUseAnchorLinks = false,
    deployFollowerInsightsOrganization = false,
    followerCount,
    userPartnerId,
    currencyFormat,
    canUseStudio,
    featureFlags,
}) => ({
    label: organizationName,
    fallbackUrl: getFullOrganizationsUrl(SETTINGS_HOME_URL_PATH),
    imageUrl: organizationImageUrl,
    avatarProps: organizationImageUrl
        ? undefined
        : {
              text: organizationName,
              backgroundColor: 'control',
              textColor: 'white',
          },
    navItems: _getUserMenuItems({
        showEventsLink,
        hideOrganizationSwitch,
        publicUserId,
        requestPath,
        serverUrl,
        email,
        fullName,
        userImageUrl,
        isStaff,
        adminServerUrl,
        hasTikibar,
        logoutUrl,
        numOrders,
        numSavedEvents,
        numFollowing,
        onlyUseAnchorLinks,
        deployFollowerInsightsOrganization,
        followerCount,
        userPartnerId,
        currencyFormat,
        canUseStudio,
        featureFlags,
    }),
});

export const getOrganizationImageUrl = (organization) =>
    get(organization, 'image.url');
